import { styled, SxProps, Theme } from '@mui/material';
import Symbol from '../Symbol';
import Tooltip from 'components/interface/tooltip/Tooltip';
import { Bars } from 'react-loader-spinner';
import React from 'react';

const StyledButton = styled('button', {
    shouldForwardProp: prop =>
        !['$variant', '$size', '$fullWidth', '$disabled', 'sx'].includes(
            prop as string
        ),
})<{
    $variant: ButtonProps['variant'];
    $size: ButtonProps['size'];
    $fullWidth?: boolean;
    $disabled?: boolean;
}>(({ theme, $variant, $size, $fullWidth, $disabled }) => {
    const variantStyles = getVariantStyles(
        $variant || 'primary',
        theme,
        $disabled || false
    );

    const sizeMap = {
        xs: { height: '24px', padding: '8px' },
        sm: { height: '32px', padding: '12px' },
        md: { height: '36px', padding: '16px' },
        lg: { height: '40px', padding: '24px' },
    };

    const currentSize = sizeMap[$size || 'md'];

    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: currentSize.height,
        gap: '8px',
        border: 'none',
        borderRadius: theme.shape.borderRadius,
        cursor: $disabled ? 'default' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        fontFamily: theme.typography.fontFamily,
        ...theme.typography.body2,
        fontWeight: 500,
        width: $fullWidth ? '100%' : 'auto',
        paddingLeft: currentSize.padding,
        paddingRight: currentSize.padding,
        userSelect: 'none',
        ...variantStyles,
    };
});

function getVariantStyles(
    variant: NonNullable<ButtonProps['variant']>,
    theme: Theme,
    disabled: boolean
) {
    if (disabled) {
        // Disabled states per variant
        switch (variant) {
            case 'primary':
            case 'primaryDark':
            case 'error':
                return {
                    backgroundColor: theme.palette.grey[300],
                    color: theme.palette.grey[500],
                };
            case 'secondary':
                return {
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.grey[400],
                };
            case 'outlined':
                return {
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    color: theme.palette.grey[400],
                };
            case 'text':
            case 'ghost':
                return {
                    backgroundColor: 'transparent',
                    color: theme.palette.grey[400],
                };
            default:
                return {};
        }
    }

    // Active states
    switch (variant) {
        case 'primary':
            return {
                'backgroundColor': theme.palette.primary.main,
                'color': '#fff',
                '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                },
            };
        case 'primaryDark':
            return {
                'backgroundColor': theme.palette.primaryDark.main,
                'color': '#fff',
                '&:hover': {
                    backgroundColor: theme.palette.primaryDark.dark,
                },
            };
        case 'error':
            return {
                'backgroundColor': theme.palette.error.main,
                'color': '#fff',
                '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                },
            };
        case 'secondary':
            return {
                'backgroundColor': theme.palette.grey[200],
                'color': theme.palette.text.primary,
                '&:hover': {
                    backgroundColor: theme.palette.grey[300],
                },
            };
        case 'outlined':
            return {
                'backgroundColor': 'rgba(255,255,255,1.0)',
                'border': `1px solid ${theme.palette.grey[400]}`,
                'color': theme.palette.text.primary,
                '&:hover': {
                    borderColor: theme.palette.grey[600],
                },
            };
        case 'text':
            return {
                'backgroundColor': 'transparent',
                'color': theme.palette.text.primary,
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
            };
        case 'ghost':
            return {
                'backgroundColor': 'transparent',
                'color': theme.palette.text.primary,
                'height': 'auto',
                'paddingLeft': 0,
                'paddingRight': 0,
                'borderRadius': 0,
                'minWidth': 0,
                'width': 'fit-content',
                'textDecoration': 'underline',
                '&:hover': {
                    backgroundColor: 'transparent',
                    color: theme.palette.primary.main,
                },
            };
        default:
            return {};
    }
}

function getSpinnerColor(variant: NonNullable<ButtonProps['variant']>): string {
    switch (variant) {
        case 'primary':
        case 'primaryDark':
        case 'error':
            return '#fff';
        case 'secondary':
        case 'outlined':
        case 'text':
        case 'ghost':
        default:
            return '#000';
    }
}

export interface ButtonProps {
    children?: React.ReactNode;
    type?: 'button' | 'submit' | 'reset';
    form?: string;
    icon?: string;
    iconPosition?: 'start' | 'end';
    iconFilled?: boolean;
    variant?:
        | 'primary'
        | 'primaryDark'
        | 'secondary'
        | 'outlined'
        | 'text'
        | 'ghost'
        | 'error';
    size?: 'xs' | 'sm' | 'md' | 'lg';
    disabled?: boolean;
    loading?: boolean;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    fullWidth?: boolean;
    sx?: SxProps<Theme>;
    tooltip?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            type = 'button',
            form,
            icon,
            iconPosition = 'start',
            iconFilled = false,
            variant = 'primary',
            size = 'md',
            disabled = false,
            loading = false,
            onClick,
            fullWidth = false,
            sx,
            tooltip,
        },
        ref
    ) => {
        const showSpinner = loading;
        const showIcon = icon && !loading;

        const spinnerColor = getSpinnerColor(variant);

        const iconElement = showSpinner ? (
            <Bars height='16' width='16' color={spinnerColor} />
        ) : showIcon ? (
            <Symbol
                symbol={icon}
                size='sm'
                color='inherit'
                filled={iconFilled}
            />
        ) : null;

        const button = (
            <StyledButton
                type={type}
                form={form}
                ref={ref}
                $variant={variant}
                $size={size}
                $fullWidth={fullWidth}
                $disabled={disabled || loading}
                disabled={disabled || loading}
                onClick={onClick}
                sx={sx}>
                {iconPosition === 'start' && iconElement}
                {children}
                {iconPosition === 'end' && iconElement}
            </StyledButton>
        );

        return tooltip ? (
            <Tooltip title={tooltip}>
                <span>{button}</span>
            </Tooltip>
        ) : (
            button
        );
    }
);

Button.displayName = 'Button';
